import { useState } from "react"
import invariant from "tiny-invariant"
import { useCurrentUser } from "~/auth/CurrentUserContext"
import { publicRosterPath } from "~/common/paths"
import { PublishMediaKitDialog } from "~/components/public-media-kit/PublishMediaKitDialog"
import { Tooltip, TooltipContent, TooltipTrigger } from "~/shadcn/ui/tooltip"

import MediaKitIcon from "~/images/icons/media-kit.svg?react"

const buttonClassName =
  "inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-slate-950 dark:focus-visible:ring-slate-300 bg-gray20 text-defaultSecondary hover:bg-gray20/60 dark:bg-slate-800 dark:text-slate-50 dark:hover:bg-slate-800/80 rounded h-10 px-[24px] py-2 flex-row gap-2"

type MediaKitState =
  | { kind: "live" }
  | { kind: "unavailable"; reason: string }
  | { kind: "publishable" }

export const MediaKitButton = () => {
  const currentUser = useCurrentUser()
  const agency = currentUser.agency
  invariant(agency, "My Roster requires an agency")
  invariant(
    agency.publicMediaKitEnabled != null,
    "An agency's own members can read its publication setting"
  )
  const [dialogOpen, setDialogOpen] = useState(false)

  // Publishing is the agency's choice (ZAN-902); a published kit is only
  // reachable while the agency also has active access.
  const state: MediaKitState = agency.publicMediaKitEnabled
    ? agency.publiclyViewable
      ? { kind: "live" }
      : {
          kind: "unavailable",
          reason:
            "Your media kit is published, but visitors can't see it until your plan is active.",
        }
    : agency.canManagePublicMediaKit.value
    ? { kind: "publishable" }
    : {
        kind: "unavailable",
        reason: "An agency admin needs to generate your media kit.",
      }

  switch (state.kind) {
    case "live":
      return (
        <a
          href={publicRosterPath({ agencySlug: agency.slug })}
          className={buttonClassName}
          target="_blank"
          rel="noopener noreferrer"
        >
          <MediaKitIcon />
          <span>Media Kit</span>
        </a>
      )
    case "publishable":
      return (
        <>
          <button
            type="button"
            className={buttonClassName}
            onClick={() => setDialogOpen(true)}
          >
            <MediaKitIcon />
            <span>Generate media kit</span>
          </button>
          <PublishMediaKitDialog
            agencyId={agency.id}
            variant={{ kind: "own", agencySlug: agency.slug }}
            open={dialogOpen}
            onOpenChange={setDialogOpen}
          />
        </>
      )
    case "unavailable":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* A disabled button gets no pointer or focus events, so the
                wrapper carries the tooltip. */}
            <span tabIndex={0} aria-label={`Media Kit: ${state.reason}`}>
              <button type="button" className={buttonClassName} disabled>
                <MediaKitIcon />
                <span>Media Kit</span>
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{state.reason}</p>
          </TooltipContent>
        </Tooltip>
      )
  }
}
